import env from '../config/env.js';

export const brand = {
  name: 'CrediToolsMX',
  tagline: 'Automatización y nube para empresas',
  siteUrl: env.SITE_URL,
  email: 'contacto@creditools.com.mx',
  phone: '+52 990 388 8082',
  whatsappHref: 'https://wa.me/529903888082',
  ogImage: `${env.SITE_URL}/logo_og.png`,
  logo: `${env.SITE_URL}/logo.png`,
  location: 'Mexico',
  socialLinks: ['https://www.facebook.com/people/Creditoolsmx/61581657555486'],
};

export const navigationItems = [
  { to: '/', label: 'Inicio' },
  { to: '/soluciones', label: 'Soluciones' },
  { to: '/herramientas', label: 'Herramientas' },
  { to: '/blog', label: 'Blog' },
  { to: '/casos-de-exito', label: 'Casos de éxito' },
  { to: '/contacto', label: 'Contacto' },
];

export const metaDefaults = {
  title: `Automatización y optimización cloud | ${brand.name}`,
  description:
    'Reducimos costos cloud, automatizamos reportes y creamos herramientas internas para que tu empresa opere con más claridad y eficiencia.',
  ogImage: brand.ogImage,
  twitterCard: 'summary_large_image',
};

export const pageMeta = {
  home: {
    title: `Reduce costos cloud y automatiza reportes | ${brand.name}`,
    description:
      'Ayudamos a dueños de negocio y gerentes a reducir costos en la nube, automatizar reportes y construir herramientas internas que ahorran tiempo real.',
    path: '/',
  },
  solutions: {
    title: `Soluciones tecnológicas para empresas | ${brand.name}`,
    description:
      'Optimización cloud, automatización de reportes y herramientas internas a medida para empresas que quieren operar con menos fricción y más control.',
    path: '/soluciones',
  },
  awsLanding: {
    title: `Reduce costos AWS sin perder rendimiento | ${brand.name}`,
    description:
      'Auditoría técnica para detectar gasto innecesario en AWS, optimizar arquitectura cloud y reducir costos mensuales sin afectar operación.',
    path: '/soluciones/reducir-costos-aws',
  },
  reportsLanding: {
    title: `Automatiza reportes gerenciales | ${brand.name}`,
    description:
      'Convierte reportes manuales, Excel y archivos dispersos en herramientas claras para auditoría, operación y toma de decisiones.',
    path: '/soluciones/automatizar-reportes',
  },
  caseStudies: {
    title: `Casos de éxito en automatización y cloud | ${brand.name}`,
    description:
      'Resultados reales en reducción de costos AWS, automatización de reportes y optimización operativa para empresas.',
    path: '/casos-de-exito',
  },
  awsCaseStudy: {
    title: `Caso de éxito: reducción de costos AWS | ${brand.name}`,
    description:
      'Cómo una empresa redujo su gasto AWS de $4,000 a menos de $2,000 MXN mensuales mediante optimización técnica.',
    path: '/casos-de-exito/aws-optimizacion',
  },
  reportsCaseStudy: {
    title: `Caso de éxito: automatización de reportes | ${brand.name}`,
    description:
      'Creamos una herramienta local para agilizar reportes de auditoría y facilitar el análisis ejecutivo de información.',
    path: '/casos-de-exito/automatizacion-reportes',
  },
  tools: {
    title: `Calculadoras y convertidores gratis | ${brand.name}`,
    description:
      'Calculadoras y convertidores gratuitos para electricidad, construcción, mecánica y negocio. Resultados al instante y sin registro.',
    path: '/herramientas',
  },
  blog: {
    title: `Blog de automatización y operaciones | ${brand.name}`,
    description:
      'Contenido práctico sobre reportes, procesos, eficiencia operativa y decisiones tecnológicas para PyMEs y equipos técnicos.',
    path: '/blog',
  },
  contact: {
    title: `Agenda un diagnóstico tecnológico | ${brand.name}`,
    description:
      'Cuéntanos tu proceso, costos cloud o reportes manuales y te ayudamos a detectar oportunidades de ahorro y automatización.',
    path: '/contacto',
  },
};

export const homeContent = {
  hero: {
    eyebrow: 'Para dueños de negocio y gerentes de operaciones',
    title: 'Reducimos tus costos en la nube y automatizamos tus reportes',
    titleHighlight: 'costos en la nube',
    subtitle:
      'Detectamos gasto innecesario en AWS, eliminamos reportes manuales y construimos herramientas que le ahorran horas reales a tu equipo cada semana.',
    primaryCta: { label: 'Agenda un diagnóstico de 15 minutos', href: '/contacto?intent=diagnostico' },
    secondaryCta: { label: 'Ver casos de éxito', href: '/casos-de-exito' },
    assurances: ['Sin compromiso comercial', 'Respuesta en máximo 1 día hábil'],
    caseCard: {
      label: 'Factura AWS mensual · caso real',
      title: 'Optimización sin perder rendimiento',
      savingPercent: 50,
      before: 4000,
      after: 2000,
      note: 'Montos en MXN por mes',
      href: '/casos-de-exito/aws-optimizacion',
      reportTitle: 'Reportes de auditoría',
      reportText: 'que antes se armaban a mano en Excel, ahora se cargan, ordenan y analizan en una sola herramienta.',
    },
  },
  trustItems: [
    { icon: 'clock', label: 'Diagnóstico de 15 min' },
    { icon: 'cloud', label: 'Auditoría AWS' },
    { icon: 'sheet', label: 'Excel y reportes manuales' },
    { icon: 'code', label: 'Herramientas web y locales' },
    { icon: 'wrench', label: 'Herramientas gratuitas' },
    { icon: 'message', label: 'Respuesta en 1 día hábil' },
  ],
  problems: [
    {
      icon: 'trend',
      symptom: 'La factura cloud sube cada mes y nadie la revisa.',
      fix: 'Auditamos servicios, consumo y recursos sobredimensionados.',
      label: 'Costos cloud',
      href: '/soluciones/reducir-costos-aws',
    },
    {
      icon: 'clock',
      symptom: 'Los reportes en Excel se comen horas cada semana.',
      fix: 'Convertimos cargas manuales en flujos automáticos.',
      label: 'Reportes',
      href: '/soluciones/automatizar-reportes',
    },
    {
      icon: 'folder',
      symptom: 'La información para dirección vive en archivos dispersos.',
      fix: 'Consolidamos datos en tableros y salidas ejecutivas.',
      label: 'Reportes',
      href: '/soluciones/automatizar-reportes',
    },
    {
      icon: 'repeat',
      symptom: 'Procesos repetitivos sin trazabilidad ni métricas.',
      fix: 'Construimos herramientas internas con captura y validación.',
      label: 'Herramientas',
      href: '/soluciones',
    },
  ],
  solutions: [
    {
      title: 'Reducción de costos cloud',
      description: 'Reducimos gasto sin afectar la operación.',
      tag: 'Más solicitado',
      icon: 'cloud',
      featured: true,
      bullets: ['Inventario de servicios activos', 'Detección de sobredimensionamiento', 'Revisión de storage, snapshots y balanceadores'],
      href: '/soluciones/reducir-costos-aws',
    },
    {
      title: 'Automatización de reportes',
      tag: 'Operación',
      icon: 'sheet',
      bullets: ['Consolidación de archivos', 'Limpieza de datos y reglas de negocio', 'Paneles y salidas ejecutivas'],
      href: '/soluciones/automatizar-reportes',
    },
    {
      title: 'Herramientas internas a medida',
      tag: 'A medida',
      icon: 'blocks',
      bullets: ['Aplicaciones locales o web', 'Flujos de captura y validación', 'Procesos con trazabilidad y métricas'],
      href: '/soluciones',
    },
  ],
  auditTerminal: {
    title: 'auditoria-aws · ejemplo',
    lines: [
      { tone: 'cmd', text: '$ creditools audit --aws' },
      { tone: 'ok', text: '✓ Inventario de servicios activos' },
      { tone: 'warn', text: '! Recursos sobredimensionados' },
      { tone: 'warn', text: '! Snapshots y volúmenes sin uso' },
      { tone: 'warn', text: '! Balanceadores duplicados' },
      { tone: 'res', text: '→ Plan de ahorro priorizado' },
    ],
  },
  process: [
    { icon: 'phone', title: 'Diagnóstico', description: 'Llamada de 15 minutos para entender tu costo o proceso más pesado.', note: 'Sin costo' },
    { icon: 'doc', title: 'Propuesta', description: 'Te decimos qué conviene atacar primero y qué puedes recuperar.', note: '1 día hábil' },
    { icon: 'code', title: 'Implementación', description: 'Optimizamos, automatizamos o construimos la herramienta.', note: 'Por etapas' },
    { icon: 'chart', title: 'Medición', description: 'Comparamos antes y después con cifras de tu propia operación.', note: 'Resultado documentado' },
  ],
  cases: {
    aws: {
      eyebrow: 'Optimización cloud',
      href: '/casos-de-exito/aws-optimizacion',
      comparison: {
        before: { label: 'Antes', total: '~$4,000 MXN' },
        after: { label: 'Después', total: '<$2,000 MXN' },
        rows: [
          { label: 'Servidores', before: { width: 100, status: 'Sobredimensionados' }, after: { width: 46, status: 'A su tamaño real' } },
          { label: 'Almacenamiento', before: { width: 70, status: 'Snapshots sin uso' }, after: { width: 28, status: 'Depurado' } },
          { label: 'Red', before: { width: 52, status: 'Balanceadores duplicados' }, after: { width: 24, status: 'Consolidada' } },
        ],
        note: 'Arrastra para comparar. Las barras son ilustrativas; los totales son del caso real.',
      },
    },
    reports: {
      eyebrow: 'Automatización de reportes',
      metric: '1 herramienta',
      metricUnit: 'en lugar de archivos dispersos',
      href: '/casos-de-exito/automatizacion-reportes',
    },
  },
  leadMagnets: {
    title: 'Resuelve una necesidad puntual ahora',
    description:
      'Prueba nuestras herramientas gratuitas y detecta cálculos o tareas repetitivas que convendría automatizar en tu operación.',
  },
  finalCta: {
    title: '¿Costos cloud opacos o reportes lentos? Hay una mejora clara esperando.',
    description: 'Agenda una conversación breve y detectemos dónde conviene actuar primero para recuperar control, tiempo y margen.',
    primaryCta: { label: 'Quiero un diagnóstico ejecutivo', href: '/contacto?intent=diagnostico' },
  },
};

export const solutionsContent = {
  hero: {
    eyebrow: 'Soluciones tecnológicas para tu empresa',
    title: 'Ayudamos a gerentes y dueños a operar con más control y menos costos',
    description:
      'Combinamos auditoría técnica, análisis operativo y desarrollo ligero para resolver gasto innecesario en la nube, reportes manuales y falta de herramientas internas adaptadas a tu negocio.',
  },
  pillars: [
    {
      title: 'Optimización tecnológica',
      description:
        'Detectamos sobrecostos, duplicidades y configuraciones poco eficientes en infraestructura, procesos y herramientas internas.',
    },
    {
      title: 'Automatización operativa',
      description:
        'Reducimos trabajo repetitivo con flujos más claros para auditoría, administración, operaciones y dirección.',
    },
    {
      title: 'Visibilidad ejecutiva',
      description:
        'Traducimos información dispersa en dashboards, reportes y entregables que sí ayudan a decidir.',
    },
  ],
  offers: [
    {
      title: 'Auditoría de costos AWS',
      bullets: ['Inventario de servicios activos', 'Detección de sobredimensionamiento', 'Revisión de storage, snapshots y balanceadores'],
      href: '/soluciones/reducir-costos-aws',
    },
    {
      title: 'Automatización de reportes y tableros',
      bullets: ['Consolidación de archivos', 'Limpieza de datos y reglas de negocio', 'Paneles y salidas ejecutivas'],
      href: '/soluciones/automatizar-reportes',
    },
    {
      title: 'Herramientas internas a medida',
      bullets: ['Aplicaciones locales o web', 'Flujos de captura y validación', 'Procesos con trazabilidad y métricas'],
      href: '/contacto?intent=diagnostico',
    },
  ],
};

export const caseStudies = {
  aws: {
    slug: 'aws-optimizacion',
    title: 'Reducción de costos AWS sin pérdida de rendimiento',
    description:
      'Una revisión técnica enfocada en consumo real, arquitectura y recursos activos permitió reducir el gasto cloud de forma medible.',
    metric: 'De ~$4,000 MXN a menos de $2,000 MXN mensuales',
    publishedAt: '2026-05-01',
    updatedAt: '2026-05-13',
    author: 'Equipo CrediToolsMX',
    section: 'Optimización cloud',
  },
  reports: {
    slug: 'automatizacion-reportes',
    title: 'Automatización de reportes para lectura ejecutiva más rápida',
    description:
      'Se desarrolló una herramienta local para cargar, ordenar, modificar y analizar reportes de auditoría con menos fricción operativa.',
    metric: 'Menos tiempo operativo y mejor interpretación gerencial',
    publishedAt: '2026-05-01',
    updatedAt: '2026-05-13',
    author: 'Equipo CrediToolsMX',
    section: 'Automatización de reportes',
  },
};

export const faqItems = [
  {
    question: '¿CrediToolsMX solo vende herramientas o también servicios?',
    answer:
      'Ofrecemos herramientas gratuitas y servicios para empresas que necesitan optimizar costos, automatizar procesos o construir herramientas internas.',
  },
  {
    question: '¿Trabajan solo con AWS?',
    answer:
      'El caso base parte de AWS, pero el enfoque es más amplio: arquitectura, costos cloud, reportes y operación interna con procesos repetitivos.',
  },
  {
    question: '¿La automatización siempre implica una plataforma compleja?',
    answer:
      'No. En muchos casos el primer paso útil es una herramienta local o web pequeña que reduzca tiempo, errores y dispersión de información.',
  },
  {
    question: '¿Qué pasa después de la llamada de 15 minutos?',
    answer:
      'Te enviamos una recomendación concreta en máximo 1 día hábil: qué conviene atacar primero y cómo. Tú decides si avanzamos.',
  },
  {
    question: '¿Cuánto tarda un proyecto típico?',
    answer:
      'Depende del alcance. Una auditoría de costos o una primera automatización suele entregarse por etapas cortas, para que veas resultados desde el inicio.',
  },
];

export const contactSection = {
  title: '¿Cuánto te está costando no revisar tu infraestructura cloud o tus reportes manuales?',
  description:
    'Cuéntanos qué parte de tu operación es lenta, costosa o difícil de medir y te proponemos una acción concreta sin compromiso.',
  email: brand.email,
  phone: brand.phone,
  website: 'www.creditools.com.mx',
};
